import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import dynamicFormService from '../../../../service/dynamicForm.service';
import {
  InputUI,
  SelectUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  CheckboxGroupUI,
  CheckboxUI,
  DialogUI,
  ButtonUI,
} from '../../../Interface';
import ContactSignature from '../../../DynamicForm/ContactSignature';

import masterDataService from '../../../../service/masterData.service';
import { Box, Container, Grid, Typography } from '@mui/material';
import companyService from '../../../../service/company.service';
import simpletaskService from '../../../../service/simpletask.service';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import {
  getDynamicFormAutocompleteValue,
  getDynamicFormFieldOptions,
  getDynamicFormInitialValues,
} from '../../../../utils/dynamicFormHelper';
import WorksheetEmailDialog from './WorksheetEmailDialog';
import useConfig from '../../../../context/ConfigContext';
import useUsers from '../../../../context/UsersContext';
import { useLoader } from '../../../../provider/LoaderProvider';

const mergeEmailRecipients = (...recipientGroups) => {
  const recipients = new Map();

  recipientGroups
    .flatMap((group) => (Array.isArray(group) ? group : String(group || '').split(/[;,]/)))
    .map((email) => String(email || '').trim())
    .filter(Boolean)
    .forEach((email) => recipients.set(email.toLowerCase(), email));

  return [...recipients.values()].join('; ');
};

const Worksheet = ({ launchContext, onSaved } = {}) => {
  const { showLoader, hideLoader } = useLoader();
  const { config, getConfig } = useConfig();
  const { user, getUser } = useUsers();
  const { dynFormId: routeFormId } = useParams();
  const dynFormId = launchContext?.formId ?? routeFormId;
  const [formsData, setFormsData] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [formValues, setValues] = useState({});
  const [projectsList, setProjectsList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [simpleTasks, setSimpleTasks] = useState([]);
  const [simpleTask, setSimpleTask] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSignatures, setContactSignatures] = useState({});
  const [customerSignature, setCustomerSignature] = useState('');
  const [userSignature, setUserSignature] = useState('');
  const [signatureResetKey, setSignatureResetKey] = useState(0);
  const [dynamicFormHeadId, setDynamicFormHeadId] = useState(null);
  const [worksheetEmailEnabled, setWorksheetEmailEnabled] = useState(false);
  const [sendWorksheetEmail, setSendWorksheetEmail] = useState(false);
  const [worksheetEmailRecipients, setWorksheetEmailRecipients] = useState('');
  const [defaultWorksheetEmailRecipients, setDefaultWorksheetEmailRecipients] = useState('');
  const [worksheetEmailTemplates, setWorksheetEmailTemplates] = useState([]);
  const [worksheetEmailDialog, setWorksheetEmailDialog] = useState({
    open: false,
    headId: null,
    recipients: '',
    templates: [],
    fixedAttachments: [],
    initialEmailValues: null,
  });
  const projectRequired = selectedForm?.projectRequired ?? true;
  const visibleFields = fields
    .filter((field) => !field.onlyWithoutProject || (!projectRequired && !project?.projectId))
    .sort((firstField, secondField) => {
      if (Boolean(firstField.systemRole) !== Boolean(secondField.systemRole)) {
        return firstField.systemRole ? -1 : 1;
      }

      return firstField.visOrder - secondField.visOrder;
    });

  // Contactok kiválasztása dialógusban
  const handleOpenContactDialog = () => setContactDialogOpen(true);
  const handleCloseContactDialog = () => setContactDialogOpen(false);
  const handleContactChange = (checkedIds) => {
    setSelectedContacts(checkedIds);
  };

  // Egy contact aláírásának mentése
  const handleSignatureChange = (contactId, dataUrl) => {
    setContactSignatures((prev) => ({ ...prev, [contactId]: dataUrl }));
  };

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(user).length) getUser();
  }, [config, getConfig, getUser, user]);

  useEffect(() => {
    masterDataService.getDynamicFroms().then((data) => {
      setFormsData(data);
    });

    masterDataService.projectToForm().then((data) => {
      setProjectsList(
        data.map((project) => ({
          label: project.projectName,
          value: project.projectId,
        }))
      );
      setProjects(data);
    });

    simpletaskService.getSimpleTasks().then((data) => {
      setSimpleTasks(data || []);
    });

  }, []);

  useEffect(() => {
    if (!dynFormId) return;

    const selectedDynamicForm = formsData.find((form) => form.id === Number(dynFormId));
    setSelectedForm(selectedDynamicForm ?? null);
  }, [dynFormId, formsData]);

  useEffect(() => {
    if (!launchContext?.projectId || !projects.length) return;
    setProject(projects.find((item) => item.projectId === launchContext.projectId) ?? null);
  }, [launchContext, projects]);

  useEffect(() => {
    if (project?.projectId) {
      const projectlessFieldIds = new Set(fields.filter((field) => field.onlyWithoutProject).map((field) => field.id));
      setValues((currentValues) =>
        Object.fromEntries(Object.entries(currentValues).filter(([fieldId]) => !projectlessFieldIds.has(Number(fieldId))))
      );

      if (!project.companyId) {
        setContacts([]);
        setSelectedContacts([]);
        addNotification({
          content: 'A kiválasztott projekt nem rendelkezik partnerrel!',
          type: NotificationType.WARNING,
        });
        return;
      }

      companyService.getCompanyById(project.companyId).then((data) => {
        setContacts(
          (data?.companyContact || []).map((contact) => ({
            label: contact.name,
            value: contact.id,
            email: contact.email,
          }))
        );
      });
    } else {
      setContacts([]);
      setSelectedContacts([]);
    }
  }, [project, fields]);

  useEffect(() => {
    let active = true;
    setWorksheetEmailTemplates([]);
    setSendWorksheetEmail(false);
    dynamicFormService.getWorksheetEmailOptions(project?.projectId, selectedForm?.id).then((data) => {
      if (!active) return;
      const templates = (data?.templates || []).filter((template) => template.id === data?.worksheetEmailTemplateId);
      setWorksheetEmailEnabled(Boolean(data?.enabled));
      setSendWorksheetEmail(Boolean(data?.sendWorksheetEmail && templates.length > 0));
      setWorksheetEmailRecipients(data?.worksheetEmailRecipients || '');
      setDefaultWorksheetEmailRecipients(data?.worksheetEmailRecipients || '');
      setWorksheetEmailTemplates(templates);
    }).catch(() => {});
    return () => { active = false; };
  }, [project?.projectId, selectedForm?.id]);

  useEffect(() => {
    let active = true;
    if (selectedForm?.id) {
      const getDynamicForm = dynFormId ? dynamicFormService.getWorksheet : dynamicFormService.getItem;
      getDynamicForm(selectedForm.id).then((data) => {
        if (!active) return;
        const dynamicFormFields = data?.dynamicFormField || [];
        setFields(dynamicFormFields);
        setValues({ ...getDynamicFormInitialValues(dynamicFormFields), ...(launchContext?.values ?? {}) });
      }).catch(() => {});
    } else {
      setFields([]);
      setValues({});
    }
    return () => { active = false; };
  }, [dynFormId, selectedForm, launchContext]);

  const setFieldValue = (fieldId, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [fieldId]: value,
    }));

    if (!project?.projectId && fields.find((field) => field.id === fieldId)?.systemRole === 'CONTACT_EMAIL') {
      setWorksheetEmailRecipients(value || '');
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'TEXT':
      case 'LONG_TEXT':
        return (
          <InputUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            multiline={field.type === 'LONG_TEXT'}
            rows={field.type === 'LONG_TEXT' ? 3 : 1}
            variant="standard"
          />
        );
      case 'DECIMAL':
        return (
          <InputUI
            label={field.name}
            required={field.required}
            type="number"
            value={formValues?.[field.id] ?? ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
      case 'COMBO':
        if (field.autocomplete || field.multiselect) {
          return (
            <AutoCompleteSelectUI
              label={field.name}
              required={field.required}
              options={getDynamicFormFieldOptions(field)}
              value={getDynamicFormAutocompleteValue(field, formValues?.[field.id])}
              multiple={field.multiselect}
              onChange={(_e, newValue) =>
                setFieldValue(
                  field.id,
                  field.multiselect ? (newValue || []).map((option) => option.value) : newValue?.value ?? null
                )
              }
              variant="standard"
            />
          );
        }
        return (
          <SelectUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] ?? ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            option={getDynamicFormFieldOptions(field)}
            variant="standard"
          />
        );
      case 'DATE':
        return (
          <DatePickerUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || null}
            onChange={(val) => setFieldValue(field.id, val)}
            variant="standard"
          />
        );
      case 'STATIC':
        return <Typography style={{ padding: '8px 0', fontStyle: 'italic' }}>{field.staticText}</Typography>;
      default:
        return (
          <InputUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
    }
  };

  // Mentés
  const handleSave = async (preview = false) => {
    if (projectRequired && !project?.projectId) {
      addNotification({ content: 'A projekt kiválasztása kötelező!', type: NotificationType.WARNING });
      return;
    }

    const missingRequiredField = visibleFields.find((field) => {
      if (!field.required || field.type === 'STATIC') return false;

      const value = formValues?.[field.id];
      return (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (missingRequiredField) {
      addNotification({
        content: `A(z) ${missingRequiredField.name} mező kitöltése kötelező!`,
        type: NotificationType.WARNING,
      });
      return;
    }

    if (sendWorksheetEmail && worksheetEmailTemplates.length === 0) {
      addNotification({ content: 'A munkalap kiküldéséhez e-mail sablon választása kötelező!', type: NotificationType.WARNING });
      return;
    }

    const selectedContactSignatures = Object.fromEntries(
      Object.entries(contactSignatures).filter(([contactId]) => selectedContacts.includes(contactId))
    );
    const selectedContactIds = new Set(selectedContacts.map(String));
    const selectedContactEmails = contacts
      .filter((contact) => selectedContactIds.has(String(contact.value)))
      .map((contact) => contact.email);
    const resolvedWorksheetEmailRecipients = project?.projectId
      ? mergeEmailRecipients(defaultWorksheetEmailRecipients, selectedContactEmails)
      : worksheetEmailRecipients;
    const payload = {
      formId: selectedForm?.id,
      projectId: project?.projectId,
      simpleTaskId: simpleTask?.simpleTaskId,
      values: formValues,
      signatures: selectedContactSignatures,
      customerSignature: selectedContacts.length === 0 ? customerSignature : null,
      userSignature,
      dynamicFormHeadId,
      isPreview: preview,
      sendWorksheetEmail,
      worksheetEmailRecipients: resolvedWorksheetEmailRecipients,
    };
    showLoader();
    dynamicFormService
      .addForm(payload)
      .then((data) => {
        addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
        if (data?.openEmailDialog) {
          setWorksheetEmailDialog({
            open: true,
            headId: data.headId,
            recipients: resolvedWorksheetEmailRecipients,
            templates: worksheetEmailTemplates,
            fixedAttachments: data.attachmentName ? [{ name: data.attachmentName }] : [],
            initialEmailValues: { to: resolvedWorksheetEmailRecipients, templateId: worksheetEmailTemplates[0]?.id ?? null },
          });
        }
        // Reset állapotok
        if (!preview) {
          setValues({});
          if (!dynFormId) setSelectedForm(null);
          setProject(null);
          setSimpleTask(null);
          setSelectedContacts([]);
          setContactSignatures({});
          setCustomerSignature('');
          setUserSignature('');
          setSignatureResetKey((currentKey) => currentKey + 1);
          if (!dynFormId) setFields([]);
          setDynamicFormHeadId(null);
          setSendWorksheetEmail(false);
          setWorksheetEmailRecipients('');
          if (!data?.openEmailDialog) onSaved?.();
          return;
        }

        addNotification({ content: 'Előnézet elkészült!', type: NotificationType.INFO });
        setDynamicFormHeadId(data.headId);

        if (data?.templatePath) {
          const url = `${process.env.REACT_APP_API_URL}/${data.templatePath}`;
          window.open(url, '_blank');
        }
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleSendWorksheetEmail = async (emailData) => {
    const { files = [], ...data } = emailData;
    await dynamicFormService.sendWorksheetEmail(worksheetEmailDialog.headId, data, files, true);
    addNotification({ content: 'A munkalap kiküldése sikeres!', type: NotificationType.SUCCESS });
    setWorksheetEmailDialog((current) => ({ ...current, open: false }));
    onSaved?.();
  };

  return (
    <>
      <Container maxWidth="md">
        <Grid mt={2} container>
          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label={`Projekt kiválasztása${projectRequired ? '' : ' (opcionális)'}`}
              options={projectsList}
              value={projectsList.find((p) => p.value === project?.projectId) || null}
              onChange={(_e, newVal) => {
                setProject(newVal?.value ? projects.find((proj) => proj.projectId === newVal.value) : null);
                setSimpleTask(null);
              }}
              placeholder="Válassz projektet"
            />
          </Grid>

          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label="Feladat kiválasztása (opcionális)"
              options={simpleTasks
                .filter((task) => task.projectId === project?.projectId)
                .map((task) => ({
                  label: task.simpleTaskName,
                  value: task.simpleTaskId,
                }))}
              value={
                simpleTask
                  ? {
                      label: simpleTask.simpleTaskName,
                      value: simpleTask.simpleTaskId,
                    }
                  : null
              }
              onChange={(_e, newVal) =>
                setSimpleTask(
                  newVal?.value
                    ? simpleTasks.find((task) => task.simpleTaskId === newVal.value) ?? null
                    : null
                )
              }
              placeholder="Válassz feladatot (opcionális)"
              disabled={!project?.projectId}
            />
          </Grid>

          {project?.projectId && (
            <Grid item xs={12} mt={2}>
              <ButtonUI
                text="Kapcsolattartók kiválasztása"
                color="blue-gray"
                variant="gradient"
                onClick={handleOpenContactDialog}
                disabled={!project?.projectId}
              />
              {selectedContacts.length > 0 && (
                <div className="mt-2 text-sm text-gray-700">
                  Kiválasztva:{' '}
                  {contacts
                    .filter((c) => selectedContacts.includes(c.value.toString()))
                    .map((c) => c.label)
                    .join(', ')}
                </div>
              )}
            </Grid>
          )}

          {visibleFields.length > 0 && (
            <>
              {visibleFields.map((field) => (
                <Grid item xs={12} mt={1} key={field.id}>
                  {renderField(field)}
                </Grid>
              ))}
            </>
          )}

          {worksheetEmailEnabled && (
            <Grid item xs={12} mt={3}>
              <CheckboxUI
                label="Munkalap kiküldése"
                checked={sendWorksheetEmail}
                onChange={(_event, checked) => setSendWorksheetEmail(checked)}
              />
            </Grid>
          )}
        </Grid>

        {/* Megrendelői aláírás a form után */}
        <Box mt={6}>
          <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Megrendelői aláírás</h3>
          {selectedContacts.length > 0 ? (
            contacts
              .filter((c) => selectedContacts.includes(c.value.toString()))
              .map((contact) => (
                <ContactSignature
                  key={`${signatureResetKey}-${contact.value}`}
                  contact={contact}
                  value={contactSignatures[contact.value]}
                  onChange={handleSignatureChange}
                />
              ))
          ) : (
            <ContactSignature
              key={`customer-${signatureResetKey}`}
              contact={{ label: 'Megrendelő', value: 'customer' }}
              value={customerSignature}
              onChange={(_contactId, dataUrl) => setCustomerSignature(dataUrl)}
            />
          )}
        </Box>

        {/* A saját oldalon mindig a bejelentkezett felhasználó ír alá. */}
        <Box mt={6}>
          <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Saját oldali aláírás</h3>
          <ContactSignature
            key={`user-${signatureResetKey}`}
            contact={{ label: 'Aláírás', value: 'currentUser' }}
            value={userSignature}
            onChange={(_userId, dataUrl) => setUserSignature(dataUrl)}
          />
        </Box>

        {/* Mentés gomb */}
        <Box mt={6} display="flex" justifyContent="center">
          <ButtonUI
            text="Mentés"
            color="green"
            variant="gradient"
            onClick={() => handleSave(false)}
            disabled={!selectedForm || (projectRequired && !project)}
          />
          <ButtonUI
            style={{ marginLeft: '2rem' }}
            text="Előnézet"
            color="blue"
            variant="gradient"
            onClick={() => handleSave(true)}
            disabled={!selectedForm || (projectRequired && !project)}
          />
        </Box>
      </Container>
      {/* Contact kiválasztó dialógus */}
      <DialogUI
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        headerContent="Kapcsolattartók kiválasztása"
        fullWidth={true}
        maxWidth="md"
      >
        <CheckboxGroupUI options={contacts} value={selectedContacts} onChange={handleContactChange} mode="multi" />
        <Box display="flex" justifyContent="center" mt={4}>
          <ButtonUI text="Kész" onClick={handleCloseContactDialog} className="bg-success" />
        </Box>
      </DialogUI>

      <WorksheetEmailDialog
        recipientAndPhotosOnly
        open={worksheetEmailDialog.open}
        onClose={() => {
          setWorksheetEmailDialog((current) => ({ ...current, open: false }));
          onSaved?.();
        }}
        onSend={handleSendWorksheetEmail}
        config={config}
        user={user}
        emailTemplates={worksheetEmailDialog.templates}
        initialEmailValues={worksheetEmailDialog.initialEmailValues}
        fixedAttachments={worksheetEmailDialog.fixedAttachments}
      />

    </>
  );
};

export default Worksheet;
